import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Label, MaskedLines } from "@/components/Reveal";
import { Icon } from "@/components/icons";
import CTASection from "@/components/CTASection";
import { IMAGES, HERO_LINES, HOME_SERVICES, PROCESS, FEATURED_PROJECTS, MEMBERSHIPS } from "@/lib/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroHome} alt="Modern high-performance building" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/60 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-28 pb-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Label>Building Science &amp; Engineering Solutions</Label>
        </motion.div>
        <h1 className="font-heading font-bold uppercase text-navy tracking-tighter leading-[0.9] mt-5 text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem]">
          <MaskedLines lines={HERO_LINES} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl text-lg text-navy/70 leading-relaxed"
        >
          AUM Building Science &amp; Engineering Ltd. provides consulting engineering services for building envelope, roofing, and waterproofing — plus BC Energy Step Code modeling and whole-building air leakage testing.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/services" data-testid="hero-services-btn"
                className="group inline-flex items-center gap-3 bg-navy text-white px-8 py-4 text-xs tracking-[0.18em] uppercase font-bold hover:bg-brand transition-colors">
            Our Services <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/about" data-testid="hero-about-btn"
                className="inline-flex items-center gap-3 border border-navy text-navy px-8 py-4 text-xs tracking-[0.18em] uppercase font-bold hover:bg-navy hover:text-white transition-colors">
            About AUM
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const AboutIntro = () => (
  <section className="bg-gradient-to-br from-[#EBF4FA] to-[#FFEFEA]" data-testid="home-about">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="flex items-baseline gap-4">
          <span className="font-heading text-brand text-xl font-semibold">01</span>
          <Label>About AUM</Label>
        </div>
        <h2 className="font-heading text-4xl md:text-5xl uppercase font-bold text-navy tracking-tight mt-5 leading-[1]">
          Science.<br />Performance.<br />Sustainability.
        </h2>
        <p className="mt-7 text-lg text-navy/70 leading-relaxed max-w-lg">
          Founded in 2011, we are a trusted firm specializing in building envelope consulting, energy modeling, and whole building air leakage testing.
        </p>
        <p className="mt-4 text-lg text-navy/70 leading-relaxed max-w-lg">
          We concentrate on cost-effective, practical solutions to building envelope performance problems — serving both Part 3 and Part 9 buildings across new construction, restoration, and retrofit projects.
        </p>
        <Link to="/about" data-testid="home-about-learn-more"
              className="group inline-flex items-center gap-3 mt-9 bg-navy text-white px-8 py-4 text-xs tracking-[0.18em] uppercase font-bold hover:bg-brand transition-colors">
          Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="overflow-hidden">
          <img src={IMAGES.aboutInterior} alt="Building interior" className="w-full h-[520px] object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Services = () => (
  <section className="bg-white" data-testid="home-services">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <Reveal className="text-center max-w-2xl mx-auto mb-16">
        <Label>Services</Label>
        <h2 className="font-heading text-4xl md:text-5xl uppercase font-bold text-navy tracking-tight mt-4">What We Do</h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-border">
        {HOME_SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.06}>
            <div className="group h-full p-8 border-r border-b border-border hover:bg-brand-light transition-colors" data-testid={`home-service-${i}`}>
              <Icon name={s.icon} className="w-10 h-10 text-navy group-hover:text-brand transition-colors" />
              <h3 className="font-heading uppercase text-navy font-semibold mt-6 text-lg leading-tight">{s.title}</h3>
              <div className="w-8 h-[2px] bg-brand my-4" />
              <p className="text-sm text-navy/60 leading-relaxed">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="flex justify-center mt-12">
        <Link to="/services" data-testid="home-view-services"
              className="group inline-flex items-center gap-3 bg-brand text-white px-8 py-4 text-xs tracking-[0.18em] uppercase font-bold hover:bg-navy transition-colors">
          View All Services <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-gradient-to-br from-[#EBF4FA] via-white to-[#FFEFEA]" data-testid="home-process">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <Reveal className="text-center mb-16">
        <Label>Our Process</Label>
        <h2 className="font-heading text-3xl md:text-5xl uppercase font-bold text-navy tracking-tight mt-4">Collaborative. Integrated. Effective.</h2>
      </Reveal>
      <div className="relative">
        <div className="hidden lg:block absolute top-7 left-[10%] right-[10%] h-px bg-navy/20" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-y-12 gap-x-6">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.1} className="relative text-center">
              <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center font-heading font-bold text-white text-lg relative z-10"
                   style={{ backgroundColor: p.color }} data-testid={`process-${p.n}`}>
                {p.n}
              </div>
              <h3 className="font-heading uppercase text-navy font-semibold mt-5 tracking-wide">{p.title}</h3>
              <p className="text-sm text-navy/60 leading-relaxed mt-2 max-w-[200px] mx-auto">{p.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Featured = () => (
  <section className="bg-white" data-testid="home-featured">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <Reveal className="flex items-end justify-between mb-14">
        <div>
          <Label>Projects</Label>
          <h2 className="font-heading text-4xl md:text-5xl uppercase font-bold text-navy tracking-tight mt-4">Featured Projects</h2>
        </div>
        <Link to="/projects" data-testid="home-view-projects"
              className="group hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-bold text-navy hover:text-brand transition-colors">
          View All Projects <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {FEATURED_PROJECTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <Link to="/projects" className="group block" data-testid={`featured-${i}`}>
              <div className="overflow-hidden aspect-[4/3]">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
              <h3 className="font-heading uppercase text-navy font-semibold mt-4 tracking-wide">{p.title}</h3>
              <p className="text-sm text-navy/50 mt-1">{p.city}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Clients = () => (
  <section className="bg-gradient-to-r from-[#EBF4FA] to-[#FFEFEA] py-16 border-y border-border" data-testid="home-memberships">
    <div className="text-center mb-10">
      <Label>Memberships &amp; Affiliations</Label>
    </div>
    <div className="marquee-viewport">
      <div className="marquee-track">
        {MEMBERSHIPS.concat(MEMBERSHIPS).map((m, i) => (
          <span key={i} className="font-heading text-outline uppercase font-bold text-5xl md:text-6xl mx-10 tracking-tight">{m.abbr}</span>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <AboutIntro />
      <Services />
      <Process />
      <Featured />
      <Clients />
      <CTASection />
    </>
  );
}
